import React from "react";
import { Box } from "@mui/material";
import { useSwipeable } from "react-swipeable";

// --- Component Imports (assuming these are in the same directory) ---
import ImageTag from "./ImageTag";
import PhotoSphere from "./PhotoSphere";
import MainImage from "./MainImage";
import PlayButton from "./PlayButton";
import ImageTypeSelect from "./ImageTypeSelect";
import VideoFrameScrubber from "./VideoFrameScrubber";
import { useCarouselContext } from "../../context/CarouselContext";

const MainDisplay: React.FC = () => {
  const {
    filteredImages,
    selectedImage,
    selectedImageIndex,
    isWebGL,
    selectedType,
    uniqueImageTypes,
    handleGoTo,
    handleImageClick,
  } = useCarouselContext();

  const handlers = useSwipeable({
    onSwipedLeft: () => handleGoTo((selectedImageIndex + 1) % filteredImages.length),
    onSwipedUp: () => handleGoTo((selectedImageIndex + 1) % filteredImages.length),
    onSwipedRight: () => handleGoTo((selectedImageIndex - 1 + filteredImages.length) % filteredImages.length),
    onSwipedDown: () => handleGoTo((selectedImageIndex - 1 + filteredImages.length) % filteredImages.length),
    trackMouse: true,
  });


  const isSpecificUcSelected = !!selectedImage && ["img_uc_dewarp", "img_uc"].includes(selectedImage.imageType || '');

  // B: The entire list we are looking at is UC (handles index -1 synced view)
  const isUcCategoryActive = filteredImages.length > 0 && filteredImages.every(img =>
    ["img_uc", "img_uc_dewarp"].includes(img.imageType || '')
  );

  const showUcView = isSpecificUcSelected || isUcCategoryActive;

  if (!selectedImage && !showUcView) {
    return null; // or a loading indicator
  }

  // If only undercarriage images are selected, render the VideoFrameScrubber
  if (showUcView) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", position: 'relative' }}>
        {/* Overlay Controls for UC view */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            right: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 100, // Higher Z-index to stay above the Video Scrubber's Paper component
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageTypeSelect />
          </Box>
        </Box>

        {/* The Video Scrubber component */}
        <VideoFrameScrubber />
      </Box>
    );
  }

  const isPano = ["img_bgr_pano", "img_pano"].includes(selectedImage?.imageType || '');

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", position: 'relative' }}>
      <Box
        key={selectedImage?.idx}
        {...(!isPano ? handlers : {})}
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          touchAction: "pan-y",
          cursor: isPano && isWebGL ? "default" : "pointer",
        }}
        onClick={() => handleImageClick(selectedImage)}
      >
        {/* Overlaid UI Elements */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            p: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 10,
            // Add a subtle gradient so text is always readable over the image
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0))'
          }}
        >
          {/* --- Left Side Controls --- */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayButton />
            <ImageTypeSelect />
          </Box>

          {/* --- Right Side Controls --- */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ImageTag />
          </Box>
        </Box>

        {/* Main Content: PhotoSphere or standard Image */}
        {isPano && isWebGL ? (
          <PhotoSphere image={selectedImage?.src || ''} />
        ) : (
          <MainImage image={selectedImage?.src} index={selectedImage?.idx || 0} />
        )}

        {/* WebGL Not Supported Warning */}
        {isPano && !isWebGL && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              right: 8,
              backgroundColor: "#ff4d4f",
              color: "white",
              textAlign: "center",
              padding: "8px 16px",
              fontWeight: 400,
              borderRadius: "4px",
              marginBottom: "8px",
              opacity: "0.8",
              zIndex: 10,
            }}
          >
            WebGL is not supported in your browser.
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MainDisplay;
